import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from 'react-router-dom';
import TrainingPopup from './TrainingPopUp'; // Import your popup component
import { navigateToCleanHome } from '../utils/navigationUtils'; // Import navigation utility
import './navbar.scss'; // Import navbar-specific styles

import {CoursesContext} from '../context/CoursesContext';

const Navbar = ({ onNavbarToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categories, dataLoaded } = useContext(CoursesContext);
  const navbarCollapseRef = useRef(null);
  
  // State to track if dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Debug output when categories change
  useEffect(() => {
    console.log('Categories updated in NavBar:', categories);
    console.log('Data loaded status:', dataLoaded);
  }, [categories, dataLoaded]);
    // Handle navbar expansion state
  useEffect(() => {
    if (navbarCollapseRef.current) {
      // Create an observer to watch for changes to the navbar's classes
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'class') {
            const isExpanded = navbarCollapseRef.current.classList.contains('show');
            if (onNavbarToggle) {
              onNavbarToggle(isExpanded);
            }
            
            // When navbar collapses, also close any open dropdown
            if (!isExpanded && isDropdownOpen) {
              setIsDropdownOpen(false);
            }
          }
        });
      });
      
      // Start observing
      observer.observe(navbarCollapseRef.current, { attributes: true });
      
      // Clean up observer on component unmount
      return () => observer.disconnect();
    }
  }, [onNavbarToggle, isDropdownOpen]);
  
  // Effect to handle clicking outside the dropdown menu to close it
  useEffect(() => {    const handleClickOutside = (event) => {
      // Find any open dropdown menus
      const openDropdown = document.querySelector('.dropdown-menu.show');
      if (openDropdown && !openDropdown.contains(event.target) && 
          !event.target.classList.contains('dropdown-toggle')) {
        // If clicking outside the dropdown and not on the toggle button
        openDropdown.classList.remove('show');
        
        // Also reset inline styles if it's our courses dropdown
        if (openDropdown.id === 'coursesDropdownMenu') {
          openDropdown.style.display = '';
          openDropdown.style.opacity = '';
          openDropdown.style.visibility = '';
        }
        
        // Also update the toggle button state
        const toggleButton = document.querySelector('.dropdown-toggle.active');
        if (toggleButton) {
          toggleButton.classList.remove('active');
          toggleButton.setAttribute('aria-expanded', 'false');
        }
        
        // Update our React state
        setIsDropdownOpen(false);
      }
    };
    
    // Handle keyboard navigation in dropdown
    const handleKeyDown = (event) => {
      const openDropdown = document.querySelector('.dropdown-menu.show');
      if (!openDropdown) return;
      
      const dropdownItems = Array.from(openDropdown.querySelectorAll('.dropdown-item'));
      if (dropdownItems.length === 0) return;
      
      const currentIndex = dropdownItems.indexOf(document.activeElement);
      
      // ESC key closes the dropdown
      if (event.key === 'Escape') {
        openDropdown.classList.remove('show');
        openDropdown.classList.remove('debug-visible');
        const toggleButton = document.querySelector('.dropdown-toggle.active');
        if (toggleButton) {
          toggleButton.classList.remove('active');
          toggleButton.setAttribute('aria-expanded', 'false');
          toggleButton.focus(); // Return focus to toggle
        }
        // Update our React state
        setIsDropdownOpen(false);
        event.preventDefault();
      }
      // Arrow down moves focus to the next item
      else if (event.key === 'ArrowDown') {
        if (currentIndex < 0 || currentIndex >= dropdownItems.length - 1) {
          dropdownItems[0].focus();
        } else {
          dropdownItems[currentIndex + 1].focus();
        }
        event.preventDefault();
      }
      // Arrow up moves focus to the previous item
      else if (event.key === 'ArrowUp') {
        if (currentIndex <= 0) {
          dropdownItems[dropdownItems.length - 1].focus();
        } else {
          dropdownItems[currentIndex - 1].focus();
        }
        event.preventDefault();
      }
    };
    
    // Add event listeners
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up on component unmount
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const [isPopupVisible, setPopupVisible] = useState(false); // State for popup visibility
  
  // Function to toggle the training popup and close the navbar
  const togglePopup = () => {
    setPopupVisible(!isPopupVisible); // Toggle popup visibility
    closeNavbarCollapse();
  };
  
  // Function to close the popup
  const closePopup = () => {
    setPopupVisible(false);
  };  
  
  // Function to close the mobile navbar when a link is clicked
  const closeNavbarCollapse = () => {
    // Check if we're in mobile view (navbar toggle is visible)
    if (navbarCollapseRef.current && navbarCollapseRef.current.classList.contains('show')) {
      // Use Bootstrap's collapse API to hide the navbar
      // This properly handles the collapsing of the menu
      import('bootstrap').then(bootstrap => {
        const bsCollapse = new bootstrap.Collapse(navbarCollapseRef.current, {
          toggle: false
        });
        bsCollapse.hide();
        
        // Notify parent that navbar is collapsed
        if (onNavbarToggle) {
          onNavbarToggle(false);
        }
      }).catch(err => {
        console.error('Failed to import bootstrap:', err);
        // Fallback to click if import fails
        const toggle = document.querySelector('.navbar-toggler');
        if (toggle) toggle.click();
      });
    }
  };
  // Function for dropdown toggle that won't collapse the main navbar
  const handleDropdownToggle = (e) => {
    // Prevent the default behavior which might interfere with Bootstrap's dropdown
    e.preventDefault();
    e.stopPropagation();
    
    // Handle keyboard navigation
    const isEnterOrSpace = e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32;
    const isClick = e.type === 'click';
    
    if (!isClick && !isEnterOrSpace && e.type === 'keydown') {
      return; // Only handle click or keyboard activation
    }
    
    // Log categories for debugging
    console.log('Categories available:', categories);
    console.log('Categories length:', categories.length);
    
    // Get the dropdown menu element directly by ID for reliability
    const dropdownMenu = document.getElementById('coursesDropdownMenu');
    const currentTarget = e.currentTarget;
    
    if (dropdownMenu) {
      // Toggle state
      const newState = !isDropdownOpen;
      setIsDropdownOpen(newState);
      
      if (newState) {
        console.log('Opening dropdown menu');
        
        // Use both class and inline styles for maximum compatibility
        dropdownMenu.classList.add('show');
        
        // Also apply inline styles as backup
        dropdownMenu.style.display = 'block';
        dropdownMenu.style.opacity = '1';
        dropdownMenu.style.visibility = 'visible';
        dropdownMenu.style.zIndex = '9999';
        
        // Position appropriately based on viewport size
        if (window.innerWidth <= 768) {
          // For mobile, position the dropdown properly
          dropdownMenu.style.position = 'static';
          dropdownMenu.style.width = '100%';
        } else {
          // For desktop, position below the button
          dropdownMenu.style.position = 'absolute';
        }
        
        // Log menu items for debugging
        setTimeout(() => {
          const menuItems = dropdownMenu.querySelectorAll('.dropdown-item');
          console.log('Menu items count:', menuItems.length);
          menuItems.forEach(item => console.log('- Item:', item.textContent));
          
          // Focus first item for keyboard navigation
          const firstItem = dropdownMenu.querySelector('.dropdown-item:not(.disabled)');
          if (firstItem) {
            console.log('Focusing first item:', firstItem.textContent);
            firstItem.focus();
          }
        }, 50);
      } else {
        console.log('Closing dropdown menu');
        // Remove the show class
        dropdownMenu.classList.remove('show');
        
        // Reset inline styles when closing
        dropdownMenu.style.display = '';
        dropdownMenu.style.opacity = '';
        dropdownMenu.style.visibility = '';
      }
      
      // Update ARIA expanded state for accessibility
      currentTarget.setAttribute('aria-expanded', newState);
      
      // Toggle active class for visual feedback
      if (newState) {
        currentTarget.classList.add('active');
      } else {
        currentTarget.classList.remove('active');
      }
    } else {
      console.error('Dropdown menu element not found by ID');
    }
  };
  
  // Function to handle keyboard navigation in dropdown items
  const handleDropdownKeyNavigation = (e) => {
    // Only process if we have an open dropdown
    const openDropdown = document.querySelector('.dropdown-menu.show');
    if (!openDropdown) return;
    
    const dropdownItems = Array.from(openDropdown.querySelectorAll('.dropdown-item'));
    if (dropdownItems.length === 0) return;
    
    const currentIndex = dropdownItems.indexOf(document.activeElement);
      // ESC key closes the dropdown
    if (e.key === 'Escape') {
      // Update both classes and styles
      openDropdown.classList.remove('show');
      
      // Update React state to ensure consistency
      setIsDropdownOpen(false);
      
      // Reset inline styles
      if (openDropdown.id === 'coursesDropdownMenu') {
        openDropdown.style.display = '';
        openDropdown.style.opacity = '';
        openDropdown.style.visibility = '';
      }
      
      const toggleButton = document.querySelector('.dropdown-toggle.active');
      if (toggleButton) {
        toggleButton.classList.remove('active');
        toggleButton.setAttribute('aria-expanded', 'false');
        toggleButton.focus(); // Return focus to toggle
      }
      e.preventDefault();
    }
    // Arrow down moves focus to the next item
    else if (e.key === 'ArrowDown') {
      if (currentIndex < 0 || currentIndex >= dropdownItems.length - 1) {
        dropdownItems[0].focus();
      } else {
        dropdownItems[currentIndex + 1].focus();
      }
      e.preventDefault();
    }
    // Arrow up moves focus to the previous item
    else if (e.key === 'ArrowUp') {
      if (currentIndex <= 0) {
        dropdownItems[dropdownItems.length - 1].focus();
      } else {
        dropdownItems[currentIndex - 1].focus();
      }
      e.preventDefault();
    }
  };
  
  // Function to scroll to contact form
  const scrollToContact = (e) => {
    e.preventDefault();
    closeNavbarCollapse();
    
    // Check if we're on the homepage
    if (location.pathname === '/') {
      // Scroll to contact section on homepage
      const contactSection = document.querySelector('.contact-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with contact hash - use hash instead of query param
      navigate('/#contact');
    }
  };

  return (
    <div className="container-fluid">
      <div className="row align-items-start">        
        {/* Left column with logo */}        
        <div className="col-md-3">          
          <a 
            className="navbar-brand brand-custom" 
            href="/"
            onClick={(e) => {
              e.preventDefault(); // Always prevent default behavior
              
              // Close navbar first to prevent UI issues during navigation
              closeNavbarCollapse();
              
              console.log('🏠 Logo clicked: Navigating to home page top');
              
              // Pass the navigate function to our utility function
              // It will handle both the navigation and scroll behavior
              navigateToCleanHome(navigate);
            }}
          >
            <img src="/assets/images/watchelmlogo-with-strap.svg" alt="Watchelm Logo" className="img-fluid" />
          </a>
        </div>
        
        {/* Right column with phone number and navigation */}
        <div className="col-md-9">
          {/* Phone Number */}
          <div className="text-end py-2">
            <div className="top-right-text">
              <span>
                <a href="tel:+441174417005" style={{color: 'inherit', textDecoration: 'none'}}>
                  <FontAwesomeIcon icon={faPhone} /> +44 (0)117 441 7005
                </a>
                {' '}
                <span style={{marginLeft: '0.75em'}}>
                  <a href="mailto:enquiries@watchelm.com" style={{color: 'inherit', textDecoration: 'none'}}>
                    <FontAwesomeIcon icon={faEnvelope} /> enquiries@watchelm.com
                  </a>
                </span>
              </span>
            </div>
          </div>
          
          {/* Navigation Bar */}
          <nav className="navbar navbar-expand-lg p-0">
            <div className="container-fluid p-0">
              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarNav"
                aria-controls="navbarNav"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
              
              <div className="collapse navbar-collapse" id="navbarNav" ref={navbarCollapseRef}>
                <ul className="navbar-nav ms-auto">
                  <li className="nav-item">
                    <Link className="nav-link text-white" onClick={togglePopup}>
                      Training
                    </Link>
                  </li>
                 
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/consulting" onClick={closeNavbarCollapse}>
                      Consultancy
                    </Link>
                  </li>                  
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/edtech" onClick={closeNavbarCollapse}>
                      EdTech Solutions
                    </Link>
                  </li>
                  <li className="nav-item dropdown">                    <Link
                      className={`nav-link dropdown-toggle text-white ${isDropdownOpen ? 'active' : ''}`}
                      to="#"
                      id="coursesDropdown"
                      role="button"
                      aria-expanded={isDropdownOpen ? "true" : "false"}
                      aria-haspopup="true"
                      aria-controls="coursesDropdownMenu"
                      onClick={handleDropdownToggle}
                      onKeyDown={handleDropdownToggle}
                    >
                      Courses <span className="ms-1" aria-hidden="true">▼</span>
                      {!dataLoaded && <span className="ms-1 small text-warning">(Loading...)</span>}
                    </Link>                    <div 
                      className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
                      id="coursesDropdownMenu"
                      aria-labelledby="coursesDropdown" 
                      role="menu" 
                      aria-orientation="vertical"
                      style={isDropdownOpen ? {display: 'block', opacity: '1', visibility: 'visible'} : {}}
                    >
                      {/* Categories items */}
                      {categories && categories.length > 0 ? (
                        categories.map((category, index) => (
                          <Link 
                            key={`cat-${index}-${category}`}
                            className="dropdown-item"
                            to={`/categories/${category}`}
                            onClick={(e) => {
                              console.log('Category clicked:', category);
                              
                              // Close dropdown menu using React state
                              setIsDropdownOpen(false);
                              
                              // Update both classes and styles for consistency
                              const dropdownMenu = document.getElementById('coursesDropdownMenu');
                              if (dropdownMenu) {
                                dropdownMenu.classList.remove('show');
                                dropdownMenu.style.display = '';
                                dropdownMenu.style.opacity = '';
                                dropdownMenu.style.visibility = '';
                              }
                              
                              // Update toggle button
                              const dropdownToggle = document.getElementById('coursesDropdown');
                              if (dropdownToggle) {
                                dropdownToggle.classList.remove('active');
                                dropdownToggle.setAttribute('aria-expanded', 'false');
                              }
                              
                              // Close the navbar
                              closeNavbarCollapse();
                            }}
                            onKeyDown={handleDropdownKeyNavigation}
                            tabIndex="0"
                            role="menuitem"
                          >
                            {category}
                          </Link>
                        ))
                      ) : (
                        <div className="dropdown-item disabled">
                          {dataLoaded ? "No categories available" : "Loading categories..."}
                        </div>
                      )}
                    </div>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/altova" onClick={closeNavbarCollapse}>
                      Altova
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/about" onClick={closeNavbarCollapse}>
                      About Us
                    </Link>
                  </li>
                  <li className="nav-item">
                    <button className="btn btn-white border rounded-pill px-3 ms-lg-3" onClick={scrollToContact}>
                      Get in Touch
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
          
          {isPopupVisible && (
            <TrainingPopup 
              categoriesProp={categories} 
              isVisible={isPopupVisible} 
              onClose={() => {
                setPopupVisible(false);
                closeNavbarCollapse();
              }}
              closeNavbarOnClick={closeNavbarCollapse}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
